
import { useStore } from '@/store'
import * as THREE from 'three'
import { defineComponent, inject, onUnmounted } from 'vue'
import { computed, watch } from 'vue'
import MaterialCreateService from '@/services/Three/material-create.service'
import References from '@/singletons/references'
import threadSettings from '@/settings/thread-settings.json'
import TextureLoadService from '@/services/Three/texture-load.service'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselThread',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const isSceneLoaded = computed(() => store.getters.isSceneLoaded)
    const configThreadId = computed(() => store.getters.configThreadId)
    const colorpickerThread = computed(() => store.getters.colorpickerThread)
    const materialCreateService: MaterialCreateService = new MaterialCreateService()
    let textureLoadService: TextureLoadService
    let texThread: THREE.Texture
    let texThreadRainbow: THREE.Texture

    emitter.on('updateConfig', updateOption)

    // prettier-ignore
    watch(() => isSceneLoaded.value, (_) => {
      initTextureLoadService()
    })

    function initTextureLoadService() {
      if (!textureLoadService || textureLoadService == undefined) {
        textureLoadService = new TextureLoadService(References.instance.getSceneReferences().maxAnisotropy)
      }
    }

    // prettier-ignore
    watch(() => configThreadId.value, (_) => { updateOption() })
    // prettier-ignore
    watch(() => colorpickerThread.value, (_) => { updateColorpicker() })

    function updateOption() {
      try {
        if (configThreadId.value) {
          setThreadColor(threadSettings.threadColors.find((x: any) => x.id == configThreadId.value))
        } else {
          setThreadColor(threadSettings.threadColors.find((x: any) => x.default && x.default === true))
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function setThreadColor(obj: any) {
      setColor(obj.value)
      setTextures(obj.value)
    }

    function setColor(hex: string | null) {
      const color = hex == 'rainbow' ? materialCreateService.createColor(0xffffff) : materialCreateService.createColor(hex)
      References.instance.getBodyMaterial().uniforms.itoColor.value = color
      References.instance.getEdgeMaterial().uniforms.itoColor.value = color
      References.instance.getFrontMaterial().uniforms.itoColor.value = color
      References.instance.getSideMaterial().uniforms.itoColor.value = color
      References.instance.getPocketMaterial().uniforms.itoColor.value = color
      References.instance.getRibbonMaterial().uniforms.itoColor.value = color
    }

    function setTextures(hex: string | null) {
      initTextureLoadService()
      if (hex == 'rainbow') {
        if (!texThreadRainbow) {
          textureLoadService.loadImage('default/ito_rainbow', (tex: THREE.Texture | null) => {
            texThreadRainbow = tex!
            updateTexture(texThreadRainbow)
          })
        } else {
          updateTexture(texThreadRainbow)
        }
      } else {
        if (!texThread) {
          textureLoadService.loadImage('default/ito', (tex: THREE.Texture | null) => {
            texThread = tex!
            updateTexture(tex)
          })
        } else {
          updateTexture(texThread)
        }
      }
    }

    function updateTexture(tex: THREE.Texture | null) {
      References.instance.getBodyMaterial().uniforms.itoTexture.value = tex
      References.instance.getEdgeMaterial().uniforms.itoTexture.value = tex
      References.instance.getRibbonMaterial().uniforms.itoTexture = { value: tex }
    }

    function updateColorpicker() {
      let color = colorpickerThread.value || "#2e2e2e"

      setColor(color)
      setTextures(color)
    }

    onUnmounted(() => {
      emitter.off('updateConfig', updateOption)
    })

    return {
      isSceneLoaded,
      configThreadId,
      colorpickerThread
    }
  }
})
